import { PLATFORM_ASSISTANT_NAME } from '@/utils/platformChat';

/**
 * Single source of truth for the name a destination is called by.
 *
 * The same page is reached from several places — the side navigation, the
 * header title, the Page History list — and each used to spell the name its
 * own way ("Todo" vs "Membership", "Messages" vs "Chat"). Everything that
 * needs to label a route reads it from here so one feature has exactly one
 * name across the app.
 *
 * Keep labels identical to the matching side-nav entry (MemberSidebar /
 * InternalSidebar). When a nav label changes, change it here too.
 */

/**
 * Path prefix -> display name. Order matters: the first prefix that matches
 * the pathname wins, so list more specific prefixes before their parents
 * (e.g. `/hub/early` before `/hub`).
 */
const PAGE_TITLE_RULES: ReadonlyArray<readonly [string, string]> = [
  // Client workspace
  ['/client/discovery/', 'Visit workspace'],
  ['/client/', 'Client details'],

  // Superclinic portal — names mirror InternalSidebar
  ['/hub/early', 'Early Hub'],
  ['/hub', 'Hub'],
  ['/business-dashboard', 'Business Dashboard'],
  ['/bookings-dashboard', 'Bookings Dashboard'],
  ['/consultation-dashboard', 'Bookings Dashboard'],
  ['/early-dashboard', 'Early Dashboard'],
  ['/appointments', 'Appointments'],
  ['/page-recordings', 'Voice recordings'],
  ['/leads', 'Discovery'],
  ['/client-tickets', 'Client Tickets'],
  ['/users', 'People Management'],
  ['/dashboard', 'Analytics'],
  ['/nutrition/planner', 'Nutrition Planner'],
  ['/builder', 'Builder'],
  ['/feedback', 'Ideas & Issues'],
  ['/knowledge-base', 'Knowledge Base'],
  ['/backoffice', 'Reference Data'],
  ['/platform-chat', PLATFORM_ASSISTANT_NAME],
  ['/preferences', 'Preferences'],
  ['/ideas', 'Ideas'],

  // Shared
  ['/chat', 'Chat'],

  // Member portal — names mirror MemberSidebar
  ['/member/home', 'Home'],
  ['/member/todo', 'Membership'],
  ['/member/memberships', 'Memberships'],
  ['/member/health-snapshot', 'Health Snapshot'],
  ['/member/pathway-details', 'Health Snapshot'],
  ['/member/biomarker-details', 'Health Snapshot'],
  ['/member/clinical-notes', "Doctor's notes"],
  ['/member/daily-checkin-dashboard', 'My Daily Log'],
  ['/member/daily-checkin-history', 'My Daily Log'],
  ['/member/daily-checkin-settings', 'My Daily Log'],
  ['/member/daily-checkin', 'My Daily Log'],
  ['/member/my-purchases', 'My Purchases'],
  ['/member/my-carts', 'My Carts'],
  ['/member/lab-test', 'Blood & Urine Test'],
  ['/member/my-data', 'Reports'],
  ['/member/benefits', 'My Benefits'],
  ['/member/family', 'My Family'],
  ['/member/gift-thrive-early', 'Gift a Membership'],
  ['/member/whats-new', "What's New?"],
  ['/member/chat', 'Chat'],
  ['/member/reports', 'Reports'],
  ['/member/wearables', 'Wearables'],
  ['/member/videos', 'Videos'],
  ['/member/marketplace', 'Marketplace'],
  ['/member/notifications', 'Notifications'],
  ['/member/knowledge-base', 'Knowledge Base'],
  ['/member/profile', 'Profile'],
  ['/member/settings', 'Settings'],
  ['/member/support', 'Support'],
  ['/member/cognitive-assessment', 'Brain Function Assessment'],
];

/**
 * Paths that exist only to bounce the user somewhere else, or that belong to
 * the signed-out flow. They render for a frame while the redirect resolves,
 * which is long enough for the header to record them, so "Login" used to show
 * up in Page History right after signing in. Nothing here is a destination a
 * user would want to return to.
 */
const HISTORY_EXCLUDED_PREFIXES: ReadonlyArray<string> = [
  '/member/login',
  '/member/signup',
  '/member/request-magic-link',
  '/member/payment-status',
  '/member/checkout',
  '/oldlogin',
  '/auth/',
];

/** Separator between a page and the sub-tab within it: `Appointments > My Calendar`. */
export const PAGE_TITLE_SEPARATOR = '›';

/** Title-case a path segment as a last resort, e.g. `thrive-check` -> `Thrive Check`. */
function humanizeSegment(pathname: string): string {
  const segment =
    (pathname.startsWith('/member/') ? pathname.split('/')[2] : pathname.split('/')[1]) || '';
  if (!segment) return 'Workspace';
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** The canonical name for `pathname`, falling back to a title-cased segment. */
export function getPageTitle(pathname: string): string {
  return (
    PAGE_TITLE_RULES.find(([prefix]) => pathname.startsWith(prefix))?.[1] ??
    humanizeSegment(pathname)
  );
}

/** Join a page and its sub-tab in the one format used across the app. */
export function formatPageTitle(title: string, subTitle?: string): string {
  return subTitle ? `${title} ${PAGE_TITLE_SEPARATOR} ${subTitle}` : title;
}

/** True when the path is a redirect stub or auth screen that history should skip. */
export function isHistoryExcludedPath(pathname: string): boolean {
  return HISTORY_EXCLUDED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}
